// react
import React from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// react-copy-to-clipboard
import { CopyToClipboard } from "react-copy-to-clipboard";

// @mui
import { useMediaQuery, Grid, Box, Dialog, DialogTitle, DialogContent, DialogActions, SwipeableDrawer, Typography, Button, TextField, IconButton, Tooltip } from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon, ContentCopyOutlined as ContentCopyOutlinedIcon } from "@mui/icons-material";

// components
import { Puller } from "components/common/Puller";
import { LoadingDialog } from "components/loading/LoadingComponent";

// A ---------------------------------------------------------------------- M

const getButtonStyle = (action) => ({
  py: 0.5,
  borderColor: "divider",
  color: action === "close" ? "error.main" : "primary.main",
  textTransform: "none",
  boxShadow: "none",
  borderRadius: 10,
  "&:hover": {
    borderColor: action === "close" ? "error.dark" : "primary.dark",
    backgroundColor: "action.hover",
  },
});

const MnemonicActionDialog = (props) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const DialogTitleContent = () => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item>
        <Typography variant="h6" fontWeight="bold">
          {props.action}
        </Typography>
      </Grid>
      <Grid item>
        <Tooltip title={t("close")} placement="top">
          <IconButton onClick={props.handleClose} color="error" edge="end">
            <CloseOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );

  const actionForm = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          margin="dense"
          id={`Mnemonic-${props.label}`}
          name={`Mnemonic-${props.label}`}
          label="24-words secret"
          required
          variant="outlined"
          fullWidth
          rows={5}
          multiline
          value={props.mnemonic}
          InputProps={{
            readOnly: props.label === "Generate",
            endAdornment: (
              <CopyToClipboard text={props.mnemonic} onCopy={props.handleCopySuccess}>
                <Tooltip title={!props.copied ? t("copy") : t("copied")} placement="top">
                  <IconButton color="primary">
                    <ContentCopyOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </CopyToClipboard>
            ),
          }}
          onChange={(e) => props.setMnemonic(e.target.value)}
        />
      </Grid>
    </Grid>
  );

  if (props.loading) return <LoadingDialog open={props.loading} />;

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={props.open}
      onClose={props.handleClose}
      onOpen={() => props.setOpen(true)}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "45%",
          borderTopLeftRadius: "4%",
          borderTopRightRadius: "4%",
        },
      }}
    >
      <Puller />
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" textAlign="center" mt={2} mb={3}>
          {props.action}
        </Typography>
        {actionForm}
        <Grid container spacing={1} mt={5}>
          <Grid item xs={12}>
            <Button fullWidth variant="outlined" sx={getButtonStyle("open")} onClick={props.handleAction} disabled={!props.mnemonic}>
              {t("confirm")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 2,
          p: 2,
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle>
        <DialogTitleContent />
      </DialogTitle>
      <DialogContent>{actionForm}</DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={getButtonStyle("close")} onClick={props.handleClose}>
          {t("cancel")}
        </Button>
        <Button variant="outlined" onClick={props.handleAction} disabled={!props.mnemonic} sx={getButtonStyle("open")}>
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MnemonicActionDialog;
